import React, { useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import {
  Sparkles, MessageSquareText, MapPin, ShoppingBag,
  FileSearch, ShieldCheck, Star, Radar, ArrowUpRight, Check,
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const fadeUp = {
  hidden: { opacity: 0, y: 28 },
  show: (i = 0) => ({ opacity: 1, y: 0, transition: { duration: 0.6, delay: i * 0.08, ease: [0.22, 1, 0.36, 1] } }),
};

const STATS = [
  { val: "84%", label: "AI Overview visibility" },
  { val: "75%", label: "Queries now conversational" },
  { val: "62%", label: "Classic link CTR drop" },
  { val: "58%", label: "Searches are zero-click" },
];

const MOMENTS = [
  { icon: MessageSquareText, title: "I Want to Know", desc: "AI engines synthesize complex research prompts by citing verified expert authority and direct-answer structures." },
  { icon: MapPin, title: "I Want to Go", desc: "Local intent searches leverage attribute-rich reviews, operating details, and verified entity trust listings." },
  { icon: ShoppingBag, title: "I Want to Buy", desc: "Conversational decision-making guides qualified prospects to your packages over unoptimized competitors." },
];

const PILLARS = [
  { icon: FileSearch, n: "01", title: "Direct-Answer Structure", desc: "Re-architecting your content with clear Q&A formatting so AI crawlers extract precise answers." },
  { icon: ShieldCheck, n: "02", title: "Attribute Trust Signals", desc: "Verified service attributes, trust signals, and structured JSON-LD entity schema." },
  { icon: Star, n: "03", title: "Review & Sentiment Optimization", desc: "Amplifying natural sentiment keywords across the review platforms AI engines query for proof." },
  { icon: Radar, n: "04", title: "AI Citation Tracking", desc: "Continuous monitoring of your brand across Google AI Overviews, ChatGPT, Perplexity & Claude." },
];

const PHASES = [
  { n: "Phase 1", title: "Audit", desc: "AI visibility audit & moment-mapping for your key services." },
  { n: "Phase 2", title: "Schema", desc: "Entity setup, Q&A formatting & JSON-LD schema deployment." },
  { n: "Phase 3", title: "Trust", desc: "Review attribute enhancement & multi-platform sentiment tuning." },
  { n: "Phase 4", title: "Scale", desc: "Continuous AI citation tracking & multi-engine optimization." },
];

const COMPARE = [
  { k: "Target queries", old: "Short 2-3 word exact keywords", neu: "Full conversational prompts & micro-moments" },
  { k: "Content architecture", old: "Keyword-stuffed longform articles", neu: "Direct-answer modules & structured schema" },
  { k: "Trust validation", old: "Quantity of third-party backlinks", neu: "Attribute-rich reviews & verified entity data" },
  { k: "Core outcome", old: "Rank in classic ten blue links", neu: "Cited as the primary AI Overview recommendation" },
];

export default function AISearchSection() {
  const [loading, setLoading] = useState(false);
  const buyAiSeo = async () => {
    setLoading(true);
    try {
      const res = await axios.post(`${API}/payments/checkout`, {
        lookup_key: "ai_search_monthly",
        origin_url: window.location.origin,
      });
      window.location.href = res.data.checkout_url;
    } catch (e) {
      toast.error("Couldn't start checkout. Please try again.");
      setLoading(false);
    }
  };
  return (
    <section id="ai-seo" className="relative border-y border-white/10 overflow-hidden">
      <div className="absolute inset-0 grid-bg opacity-30" />
      <div className="absolute -top-40 right-0 w-[520px] h-[520px] rounded-full bg-[#1E90FF]/10 blur-[120px] pointer-events-none" />
      <div className="relative max-w-7xl mx-auto px-5 sm:px-8 py-24 sm:py-32">
        {/* header */}
        <div className="flex items-center gap-3 mb-5">
          <Sparkles size={16} className="text-[#1E90FF]" />
          <span className="text-xs tracking-[0.25em] uppercase text-[#1E90FF] font-mono-ibm">Next-Gen SEO Service</span>
        </div>
        <div className="grid lg:grid-cols-2 gap-10 items-end mb-16">
          <h2 className="font-display font-black text-4xl sm:text-6xl tracking-tighter leading-[0.95]">
            AI Search<br />Optimization
          </h2>
          <p className="text-white/60 text-lg leading-relaxed max-w-xl">
            Capturing high-intent customers in the era of Generative AI & AI Overviews. Traditional SEO relied on exact-match keywords and backlinks — engines like <span className="text-white">Google AI Overviews, ChatGPT & Perplexity</span> now evaluate context completely differently.
          </p>
        </div>

        {/* stats */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-px bg-white/10 border border-white/10 mb-6">
          {STATS.map((s, i) => (
            <motion.div key={s.label} initial="hidden" whileInView="show" viewport={{ once: true }} variants={fadeUp} custom={i}
              className="bg-[#0d0d0d] p-7" data-testid={`aiseo-stat-${i}`}>
              <p className="font-display font-black text-4xl sm:text-5xl text-[#1E90FF] tracking-tighter">{s.val}</p>
              <p className="text-white/55 text-sm mt-2">{s.label}</p>
            </motion.div>
          ))}
        </div>
        <div className="border border-[#22C55E]/40 bg-[#0f1410] p-7 flex flex-col sm:flex-row sm:items-center gap-4 mb-20">
          <p className="font-display font-black text-4xl text-[#22C55E] tracking-tighter shrink-0">7.2×</p>
          <p className="text-white/70 leading-relaxed">Higher intent lead quality from conversational prompts. Prospects typing multi-word queries possess immediate purchase intent — we position your business directly inside AI recommendations to capture them.</p>
        </div>

        {/* micro-moments */}
        <h3 className="font-display font-bold text-2xl sm:text-3xl tracking-tight mb-8">Winning the micro-moments that matter.</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-white/10 border border-white/10 mb-20">
          {MOMENTS.map((m, i) => {
            const Icon = m.icon;
            return (
              <motion.div key={m.title} initial="hidden" whileInView="show" viewport={{ once: true }} variants={fadeUp} custom={i}
                className="bg-[#0d0d0d] p-8 hover:bg-[#111] transition-colors" data-testid={`aiseo-moment-${i}`}>
                <Icon className="text-[#1E90FF] mb-5" size={28} />
                <h4 className="font-display font-bold text-xl mb-2">{m.title}</h4>
                <p className="text-white/55 leading-relaxed text-sm">{m.desc}</p>
              </motion.div>
            );
          })}
        </div>

        {/* 4-pillar framework */}
        <div className="flex items-center gap-3 mb-5">
          <span className="h-px w-10 bg-[#22C55E]" />
          <span className="text-xs tracking-[0.25em] uppercase text-[#22C55E] font-mono-ibm">Our GEO Framework</span>
        </div>
        <h3 className="font-display font-bold text-2xl sm:text-3xl tracking-tight mb-8">The 4-Pillar Generative Engine Optimization framework.</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-px bg-white/10 border border-white/10 mb-20">
          {PILLARS.map((p, i) => {
            const Icon = p.icon;
            return (
              <motion.div key={p.title} initial="hidden" whileInView="show" viewport={{ once: true }} variants={fadeUp} custom={i}
                className="bg-[#0d0d0d] p-8 flex gap-5 hover:bg-[#111] transition-colors" data-testid={`aiseo-pillar-${i}`}>
                <span className="font-display font-black text-4xl text-white/10">{p.n}</span>
                <div>
                  <Icon className="text-[#22C55E] mb-3" size={24} />
                  <h4 className="font-display font-bold text-xl mb-2">{p.title}</h4>
                  <p className="text-white/55 leading-relaxed text-sm">{p.desc}</p>
                </div>
              </motion.div>
            );
          })}
        </div>

        {/* comparison */}
        <h3 className="font-display font-bold text-2xl sm:text-3xl tracking-tight mb-8">Traditional SEO vs. AI Search Optimization.</h3>
        <div className="border border-white/10 overflow-hidden mb-20">
          <div className="grid grid-cols-3 bg-[#111] text-xs sm:text-sm font-mono-ibm uppercase tracking-wider">
            <div className="p-4 text-white/40">Focus</div>
            <div className="p-4 text-white/40 border-l border-white/10">Traditional SEO</div>
            <div className="p-4 text-[#22C55E] border-l border-white/10">Our AI Optimization</div>
          </div>
          {COMPARE.map((r, i) => (
            <div key={r.k} className={`grid grid-cols-3 text-sm ${i % 2 ? "bg-[#0d0d0d]" : "bg-[#0a0a0a]"} border-t border-white/10`}>
              <div className="p-4 font-medium">{r.k}</div>
              <div className="p-4 text-white/50 border-l border-white/10">{r.old}</div>
              <div className="p-4 text-white/80 border-l border-white/10">{r.neu}</div>
            </div>
          ))}
        </div>

        {/* roadmap */}
        <h3 className="font-display font-bold text-2xl sm:text-3xl tracking-tight mb-8">Your 4-phase service roadmap.</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-px bg-white/10 border border-white/10 mb-16">
          {PHASES.map((p, i) => (
            <motion.div key={p.n} initial="hidden" whileInView="show" viewport={{ once: true }} variants={fadeUp} custom={i}
              className="bg-[#0d0d0d] p-8 hover:bg-[#111] transition-colors" data-testid={`aiseo-phase-${i}`}>
              <span className="text-xs tracking-[0.2em] uppercase text-[#1E90FF] font-mono-ibm">{p.n}</span>
              <h4 className="font-display font-bold text-2xl mt-2 mb-2">{p.title}</h4>
              <p className="text-white/55 leading-relaxed text-sm">{p.desc}</p>
            </motion.div>
          ))}
        </div>

        {/* CTA + buyable package */}
        <div className="grid lg:grid-cols-5 gap-6 items-stretch">
          <div className="lg:col-span-3 border border-white/10 bg-gradient-to-r from-[#0d0d0d] to-[#0a0f14] p-10 sm:p-14 flex flex-col justify-center">
            <h3 className="font-display font-black text-3xl sm:text-4xl tracking-tighter mb-3">Ready to own AI Search?</h3>
            <p className="text-white/60 max-w-xl leading-relaxed mb-8">While competitors waste budget on obsolete keywords, we position your business as the default AI recommendation. Start capturing high-intent buyers today.</p>
            <a href="#contact" className="border border-white/20 text-white font-semibold px-8 py-4 rounded-sm inline-flex items-center gap-2 hover:bg-white/5 transition-colors self-start" data-testid="aiseo-cta-button">
              Book a Free AI Visibility Audit <ArrowUpRight size={18} />
            </a>
          </div>

          <div className="lg:col-span-2 relative border border-[#22C55E]/50 bg-[#0f1410] p-8 sm:p-10 flex flex-col" data-testid="aiseo-plan-card">
            <span className="absolute -top-3 left-8 bg-[#22C55E] text-black text-[10px] font-semibold tracking-wider uppercase px-3 py-1">Managed Service</span>
            <span className="text-xs tracking-[0.2em] uppercase text-[#1E90FF] font-mono-ibm">Generative Engine Optimization</span>
            <h3 className="font-display font-black text-2xl tracking-tighter mt-3">AI Search Optimization</h3>
            <div className="mt-4 mb-1 flex items-baseline gap-1">
              <span className="font-display font-black text-4xl tracking-tighter text-[#22C55E]">$499</span>
              <span className="text-white/40 text-lg">/mo</span>
            </div>
            <p className="text-[#1E90FF] text-sm font-medium">+ $150 one-time setup</p>
            <p className="text-white/40 text-sm mb-6 mt-1">Billed monthly · cancel anytime</p>
            <ul className="space-y-3 flex-1 mb-8">
              {["Full 4-Pillar GEO framework", "JSON-LD schema & direct-answer setup", "Review & sentiment optimization", "AI citation tracking across engines", "Monthly visibility reporting"].map((f) => (
                <li key={f} className="flex items-start gap-3 text-white/70 text-sm">
                  <Check size={16} className="text-[#22C55E] mt-0.5 shrink-0" /> {f}
                </li>
              ))}
            </ul>
            <button onClick={buyAiSeo} disabled={loading}
              className="green-glow w-full bg-[#22C55E] text-black font-semibold px-6 py-3.5 rounded-sm flex items-center justify-center gap-2 hover:bg-[#1fb455] transition-colors disabled:opacity-60"
              data-testid="aiseo-buy-button">
              {loading ? "Redirecting..." : <>Subscribe Now <ArrowUpRight size={16} /></>}
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}
